// ANGEL FLIGHT — EnemySystem: wave director + the advancing horde + enemy AI.
//
// Owns the authoritative live-enemy list (ctx.enemies). Schedules the mission's
// waves on the run clock; each zombie spawns at the far plane in a lateral lane
// and CLOSES on the gunner (z decreases). Behaviour branches make the horde
// tactical instead of a marching column:
//   • march  — straight advance + idle weave (filler).
//   • flank  — drifts toward a lateral edge so it comes from the side.
//   • rush   — accelerates into a lunge once it's close (runner).
//   • shoot  — halts at standoff, telegraphs, then fires straight rockets (RPG).
//   • lob    — like shoot but a slow, arcing, lower-damage acid glob (spitter).
// Ranged enemies spawn POOLED projectiles that advance in world-z each frame and
// bite the bird at the strike plane. When a melee enemy reaches the strike plane
// it bites the gunner and dies. Dead enemies (killed by the gun) are swept.
//
// Pure-ish data + deterministic (ctx.rng, never Math.random) so it stays
// testable. The WorldView renders from the list; the lead draws the telegraph
// glow + incoming-projectile tracer by listening to the new bus events.

import { BALANCE } from '../config/balance';
import { getDifficulty } from '../config/difficulty';
import { getBehavior, getEnemy, isFriendly } from '../config/enemies';
import type { EnemyBehavior, EnemyDef } from '../config/enemies';
import { ObjectPool } from '../core/Pool';
import type { EnemyId, WorldPos } from '../core/types';
import type { GameContext, GameSystem, LiveEnemy } from './System';

interface ScheduledWave {
  atMs: number;
  spawns: Array<{ id: EnemyId; count: number }>;
  fired: boolean;
  /** Authored HUD callout for this wave ('CONTACT', 'BRUTE', …), if any. */
  banner?: string;
}

/** Wave arrival shapes (G2): picked once per firing wave via ctx.rng. */
type FormationId = 'line' | 'wedge' | 'cluster' | 'pincer';

/** One enemy's slot inside its wave's formation. */
interface FormationSlot {
  formation: FormationId;
  idx: number;
  n: number;
  /** cluster: the blob's centre lane (m). */
  centerX: number;
}

/** A wave member waiting on the spawn-stagger clock (the trickle-in). */
interface PendingSpawn {
  id: EnemyId;
  slot: FormationSlot;
  /** Run-clock ms at which this unit actually materialises. */
  dueAtMs: number;
}

/** A live enemy projectile (rocket / acid glob) closing on the strike plane. */
interface EnemyProjectile {
  id: number;
  enemyId: number; // the shooter (for VFX correlation)
  pos: WorldPos;
  speed: number; // metres/sec of closing z
  damage: number;
  lob: boolean; // arcing acid (vs. flat rocket) — drives the visual
  flareable: boolean; // a heat-seeking rocket — decoyed by a live flare window
  active: boolean;
}

function makeProjectile(): EnemyProjectile {
  return {
    id: 0,
    enemyId: 0,
    pos: { x: 0, z: 0, y: 0 },
    speed: 0,
    damage: 0,
    lob: false,
    flareable: false,
    active: false,
  };
}

function resetProjectile(p: EnemyProjectile): void {
  p.active = false;
  p.enemyId = 0;
  p.speed = 0;
  p.damage = 0;
  p.lob = false;
  p.flareable = false;
}

export class EnemySystem implements GameSystem {
  private ctx!: GameContext;
  private waves: ScheduledWave[] = [];
  private densityMult = 1;
  private nextId = 1;
  private nextProjId = 1;

  /** Wave members queued on the spawn-stagger clock: a fired wave TRICKLES
   *  onto the field (one unit per spawnStaggerMs) instead of materialising as
   *  one stacked wall in a single tick. */
  private readonly pendingSpawns: PendingSpawn[] = [];

  /** Live enemy projectiles in flight. Pooled to stay zero-GC. */
  private readonly projectiles: EnemyProjectile[] = [];
  private readonly projPool = new ObjectPool<EnemyProjectile>(
    makeProjectile,
    resetProjectile,
    BALANCE.pools.enemyProjectiles,
  );

  init(ctx: GameContext): void {
    this.ctx = ctx;
    this.densityMult = getDifficulty(ctx.difficulty).densityMult;
    this.waves = ctx.mission.waves.map((w) => ({
      atMs: w.atMs,
      fired: false,
      banner: w.banner,
      spawns: (Object.entries(w.spawns) as Array<[EnemyId, number]>).map(([id, count]) => ({
        id,
        count,
      })),
    }));
  }

  update(dt: number): void {
    const ctx = this.ctx;
    const t = ctx.run.elapsedMs;
    const dtSec = dt / 1000;

    // fire due waves — each wave QUEUES its members on the stagger clock
    // (idx * spawnStaggerMs) so the wave trickles in through the fog rather
    // than popping onto the field as one stacked wall in a single tick.
    for (const w of this.waves) {
      if (!w.fired && t >= w.atMs) {
        w.fired = true;
        // The authored wave beat ('CONTACT', 'FRIENDLIES IN THE OPEN', 'BRUTE',
        // 'THE SURGE') fires WITH the wave — the HUD renders it as a callout.
        if (w.banner) ctx.bus.emit('wave:banner', { text: w.banner });
        // Each wave arrives in ONE formation (line/wedge/cluster/pincer) so
        // reinforcements read as a manoeuvre, not a straight row (G2).
        const counts = w.spawns.map((s) => Math.max(1, Math.round(s.count * this.densityMult)));
        const total = counts.reduce((a, b) => a + b, 0);
        const formation = this.pickFormation();
        const centerX =
          (ctx.rng.next() * 2 - 1) *
          BALANCE.proj.halfWidth *
          BALANCE.enemies.behavior.formations.centerSpread;
        let idx = 0;
        for (let si = 0; si < w.spawns.length; si++) {
          const s = w.spawns[si];
          const n = counts[si] ?? 0;
          if (!s) continue;
          for (let i = 0; i < n; i++) {
            const slot: FormationSlot = { formation, idx, n: total, centerX };
            this.pendingSpawns.push({
              id: s.id,
              slot,
              dueAtMs: t + idx * BALANCE.enemies.spawnStaggerMs,
            });
            idx++;
          }
        }
      }
    }

    // drain due queued spawns (in queue order — the rng stream stays stable)
    for (let i = 0; i < this.pendingSpawns.length; i++) {
      const p = this.pendingSpawns[i] as PendingSpawn;
      if (p.dueAtMs > t) continue;
      this.spawn(p.id, p.slot);
      this.pendingSpawns.splice(i, 1);
      i--;
    }

    // advance + behave + strike + sweep
    const list = ctx.enemies;
    // The bird's forward traverse: EVERY ground unit drifts toward the strike
    // plane at worldDriftMps on top of its own legs — the same rate the render
    // ground scrolls at, so relative to the terrain nobody ever moves backward
    // (parked shooters stand their ground; the helicopter overtakes them).
    const driftM = BALANCE.enemies.worldDriftMps * dtSec;
    for (let i = list.length - 1; i >= 0; i--) {
      const e = list[i] as LiveEnemy;
      if (e.dead) {
        this.recycle(i);
        continue;
      }
      e.pos.z -= driftM;
      const def = getEnemy(e.def);
      const struck = this.advance(e, def, dt, dtSec, t);
      if (struck) {
        // BLUEFOR friendlies are allies: they walk past the strike plane and off
        // the lane WITHOUT biting the bird (no hull:damaged, no combo break, no
        // shake). Hostiles bite as before.
        if (!isFriendly(def)) this.strike(e, def.contactDamage);
        e.dead = true;
        this.recycle(i);
      }
    }

    // advance enemy projectiles toward the strike plane
    this.updateProjectiles(dtSec);
  }

  /** Run the enemy's behaviour for this frame. Returns true if it reached the
   *  strike plane and should bite the gunner (melee). Ranged enemies never
   *  return true — they halt and fire instead. */
  private advance(e: LiveEnemy, def: EnemyDef, dt: number, dtSec: number, t: number): boolean {
    const behavior = getBehavior(def);
    if (e.spawnZ === undefined) e.spawnZ = e.pos.z;

    switch (behavior) {
      case 'rush':
        this.stepRush(e, def, dtSec);
        break;
      case 'flank':
        // An explicit flanker always commits to an edge.
        if (e.flankTargetX === undefined) e.flankTargetX = e.pos.x >= 0 ? BALANCE.proj.halfWidth : -BALANCE.proj.halfWidth;
        this.stepFlank(e, def, dtSec);
        break;
      case 'shoot':
      case 'lob':
        return this.stepRanged(e, def, dt, dtSec, behavior, t);
      case 'march':
      default:
        this.stepMarch(e, def, dtSec, t);
        break;
    }
    return e.pos.z <= BALANCE.enemies.strikeZ;
  }

  /** The unit's own leg speed: the type's base speed × its per-spawn jitter
   *  roll (BALANCE.enemies.behavior.speedJitter) — units of one type spread
   *  out instead of marching in lockstep. */
  private legSpeed(e: LiveEnemy, def: EnemyDef): number {
    return def.speed * (e.speedMult ?? 1);
  }

  /** Default: straight advance toward the gunner + an idle lateral weave. A
   *  fraction of marchers are tagged FLANKERS at spawn (flankTargetX set) — those
   *  also slide toward their lateral edge, so the filler horde fans into a pincer
   *  instead of a column. */
  private stepMarch(e: LiveEnemy, def: EnemyDef, dtSec: number, t: number): void {
    e.pos.z -= this.legSpeed(e, def) * dtSec;
    if (e.flankTargetX !== undefined) {
      const drift = BALANCE.enemies.behavior.flankDriftMps * dtSec;
      const d = e.flankTargetX - e.pos.x;
      e.pos.x += Math.sign(d) * Math.min(Math.abs(d), drift);
    } else {
      e.pos.x += Math.sin((t + e.id * 137) / 600) * 0.15 * dtSec;
    }
  }

  /** Rush: marches at base speed until inside rushRangeZ, then ramps a speed
   *  multiplier toward rushSpeedMult as it closes — a charging lunge. */
  private stepRush(e: LiveEnemy, def: EnemyDef, dtSec: number): void {
    const range = def.rushRangeZ ?? 10;
    const maxMult = def.rushSpeedMult ?? 2.2;
    let mult = 1;
    if (e.pos.z < range) {
      // 0 at the range edge → 1 at the strike plane.
      const near = clamp01((range - e.pos.z) / Math.max(0.001, range - BALANCE.enemies.strikeZ));
      mult = 1 + (maxMult - 1) * near;
    }
    e.pos.z -= this.legSpeed(e, def) * mult * dtSec;
  }

  /** Flank: advances while sliding its x toward the nearest lateral edge, so it
   *  tries to come at the bird from the side rather than straight on. */
  private stepFlank(e: LiveEnemy, def: EnemyDef, dtSec: number): void {
    e.pos.z -= this.legSpeed(e, def) * dtSec;
    const edge = BALANCE.proj.halfWidth;
    if (e.flankTargetX === undefined) {
      // Commit to whichever edge this enemy already leans toward (centre → +).
      e.flankTargetX = e.pos.x >= 0 ? edge : -edge;
    }
    const target = e.flankTargetX;
    const drift = BALANCE.enemies.behavior.flankDriftMps * dtSec;
    const d = target - e.pos.x;
    e.pos.x += Math.sign(d) * Math.min(Math.abs(d), drift);
  }

  /** Ranged (shoot/lob): close to standoff depth, STOP, telegraph the first
   *  shot, then fire on a cooldown. Returns false always (never melee-strikes).
   *  If it somehow drifts past the strike plane it bites like a melee, so this
   *  returns true in that defensive edge case. */
  private stepRanged(
    e: LiveEnemy,
    def: EnemyDef,
    dt: number,
    dtSec: number,
    behavior: EnemyBehavior,
    t: number,
  ): boolean {
    const standoff = def.standoffZ ?? 12;
    if (e.state === undefined) e.state = 'closing';

    // Parked at the line: strafe laterally so the shooter holds the LINE, not
    // the spot — it never statues mid-field (the audited "stops moving" read).
    if (e.state !== 'closing') this.stepStandoffStrafe(e, t);

    if (e.state === 'closing') {
      e.pos.z -= this.legSpeed(e, def) * dtSec;
      if (e.pos.z <= standoff) {
        e.pos.z = standoff; // park at the firing line
        e.standoffAnchorX = e.pos.x;
        e.state = 'telegraphing';
        e.telegraphCdMs = def.telegraphMs ?? 700;
        this.ctx.bus.emit('enemy:telegraph', {
          id: e.id,
          from: { x: e.pos.x, z: e.pos.z, y: e.pos.y },
          durationMs: e.telegraphCdMs,
        });
      }
      // Still closing — can't strike from out here (standoff >> strikeZ).
      return e.pos.z <= BALANCE.enemies.strikeZ;
    }

    if (e.state === 'telegraphing') {
      e.telegraphCdMs = (e.telegraphCdMs ?? 0) - dt;
      if (e.telegraphCdMs <= 0) {
        this.fireProjectile(e, def, behavior);
        e.state = 'firing';
        e.fireCdMs = def.fireIntervalMs ?? 1800;
      }
      // The world drift carries even a parked shooter toward the bird — one
      // that survives long enough to be overtaken bites like a melee.
      return e.pos.z <= BALANCE.enemies.strikeZ;
    }

    // firing: hold the line, fire on a cadence.
    e.fireCdMs = (e.fireCdMs ?? 0) - dt;
    if (e.fireCdMs <= 0) {
      this.fireProjectile(e, def, behavior);
      e.fireCdMs = def.fireIntervalMs ?? 1800;
    }
    return e.pos.z <= BALANCE.enemies.strikeZ;
  }

  /** Lateral weave around the parked anchor (position-from-time, so it stays
   *  deterministic per seed). Peak lateral speed ≈ standoffStrafeMps. */
  private stepStandoffStrafe(e: LiveEnemy, t: number): void {
    const B = BALANCE.enemies.behavior;
    if (e.standoffAnchorX === undefined) e.standoffAnchorX = e.pos.x;
    const phase = t * 0.001 * (B.standoffStrafeMps / B.standoffStrafeSpanM) + e.id * 1.7;
    const half = BALANCE.proj.halfWidth;
    const x = e.standoffAnchorX + Math.sin(phase) * B.standoffStrafeSpanM;
    e.pos.x = Math.max(-half, Math.min(half, x));
  }

  /** Mint a pooled projectile travelling straight down world-z toward the bird. */
  private fireProjectile(e: LiveEnemy, def: EnemyDef, behavior: EnemyBehavior): void {
    if (this.projectiles.length >= BALANCE.enemies.behavior.maxProjectiles) return;
    const p = this.projPool.acquire();
    p.id = this.nextProjId++;
    p.enemyId = e.id;
    p.pos.x = e.pos.x;
    p.pos.z = e.pos.z;
    p.pos.y = e.pos.y;
    p.speed = def.projectileSpeed ?? 12;
    p.damage = def.projectileDamage ?? def.contactDamage;
    p.lob = behavior === 'lob';
    p.flareable = def.flareable === true;
    p.active = true;
    this.projectiles.push(p);
    this.ctx.bus.emit('enemy:firedProjectile', {
      id: p.id,
      enemyId: e.id,
      from: { x: p.pos.x, z: p.pos.z, y: p.pos.y },
      toGunner: true,
      lob: p.lob,
      flareable: p.flareable,
    });
  }

  /** Advance every live projectile; one that reaches the strike plane bites the
   *  bird (gunner.damage + hull:damaged + shake, exactly like a melee strike). */
  private updateProjectiles(dtSec: number): void {
    const ctx = this.ctx;
    const list = this.projectiles;
    const flaresLive = (ctx.gunner?.flareActiveMs ?? 0) > 0;
    const driftM = BALANCE.enemies.worldDriftMps * dtSec;
    for (let i = list.length - 1; i >= 0; i--) {
      const p = list[i] as EnemyProjectile;
      // A live flare window decoys EVERY flare-able rocket in flight — the
      // forgiving timing is the design (see BALANCE.flares).
      if (flaresLive && p.flareable) {
        ctx.bus.emit('projectile:decoyed', {
          id: p.id,
          world: { x: p.pos.x, z: p.pos.z, y: p.pos.y },
        });
        this.removeProjectileAt(i);
        continue;
      }
      // The bird flies INTO incoming fire: drift adds to the closing rate.
      p.pos.z -= (p.speed * dtSec + driftM);
      if (p.pos.z <= BALANCE.enemies.strikeZ) {
        if (ctx.gunner) {
          const downed = ctx.gunner.damage(p.damage);
          ctx.bus.emit('hull:damaged', { hull: ctx.gunner.hull, maxHull: ctx.gunner.maxHull });
          ctx.bus.emit('projectile:hitGunner', { id: p.id, damage: p.damage });
          ctx.bus.emit('fx:shake', { trauma: BALANCE.juice.shakeOnHurt });
          if (downed) ctx.bus.emit('hull:down', {});
        }
        this.removeProjectileAt(i);
      }
    }
  }

  /** Apply a melee strike: bite the gunner + the same juice the projectile path
   *  emits, mirroring the original behaviour. */
  private strike(_e: LiveEnemy, contactDamage: number): void {
    const ctx = this.ctx;
    if (!ctx.gunner) return;
    const downed = ctx.gunner.damage(contactDamage);
    ctx.bus.emit('hull:damaged', { hull: ctx.gunner.hull, maxHull: ctx.gunner.maxHull });
    ctx.bus.emit('fx:shake', { trauma: BALANCE.juice.shakeOnHurt });
    if (downed) ctx.bus.emit('hull:down', {});
  }

  private removeProjectileAt(i: number): void {
    const list = this.projectiles;
    const p = list[i] as EnemyProjectile;
    const last = list[list.length - 1] as EnemyProjectile;
    list[i] = last;
    list.pop();
    this.projPool.release(p);
  }

  private recycle(i: number): void {
    this.ctx.enemies.splice(i, 1);
  }

  /** Roll the wave's arrival shape from the configured weights (ctx.rng — the
   *  sim stream, so two same-seed sims pick identical formations). */
  private pickFormation(): FormationId {
    const weights = BALANCE.enemies.behavior.formations.weights;
    const entries = Object.entries(weights) as Array<[FormationId, number]>;
    const sum = entries.reduce((a, [, w]) => a + w, 0);
    let roll = this.ctx.rng.next() * sum;
    for (const [fid, w] of entries) {
      roll -= w;
      if (roll < 0) return fid;
    }
    return 'line';
  }

  private spawn(id: EnemyId, slot?: FormationSlot): void {
    const ctx = this.ctx;
    if (ctx.enemies.length >= BALANCE.enemies.maxLive) return;
    // Roll lane + depth unconditionally (a stable per-spawn RNG draw count —
    // formation shapes reuse them as jitter instead of skipping draws).
    const laneRoll = ctx.rng.next() * 2 - 1; // -1..1
    const zRoll = ctx.rng.next(); // 0..1
    const half = BALANCE.proj.halfWidth;
    const pad = BALANCE.enemies.spawnDepthPadM;
    // Spawns materialise AT or BEYOND the far plane, inside the battlefield
    // haze — they're already in position and WALK IN through the fog, never
    // popping into the open midfield (the audited "swap-in" read).
    let lane = laneRoll * half;
    let z = BALANCE.proj.farZ + zRoll * pad;
    if (slot && slot.formation !== 'line') {
      const F = BALANCE.enemies.behavior.formations;
      const baseZ = BALANCE.proj.farZ * F.baseZFrac;
      // -1..1 across the wave (0 when alone).
      const t = slot.n <= 1 ? 0 : (slot.idx / (slot.n - 1)) * 2 - 1;
      if (slot.formation === 'wedge') {
        // A V pointing at the bird: centre leads, edges trail deeper.
        lane = t * half * 0.8 + laneRoll * 0.6;
        z = baseZ + Math.abs(t) * F.wedgeDepth + zRoll * 0.8;
      } else if (slot.formation === 'cluster') {
        // A tight blob around the picked centre lane, staggered in ranks.
        lane = slot.centerX + laneRoll * F.clusterRadius;
        z = baseZ + slot.idx * F.rankStagger * 0.5 + zRoll * 0.8;
      } else {
        // pincer: alternate slots hug the two lateral edges in ranked files.
        const side = slot.idx % 2 === 0 ? 1 : -1;
        lane = side * half * F.pincerEdge + laneRoll * 1.2;
        z = baseZ + Math.floor(slot.idx / 2) * F.rankStagger + zRoll * 0.8;
      }
      lane = Math.max(-half, Math.min(half, lane));
      // Shapes may stagger DEEPER than the far plane (they walk in through the
      // haze) but never beyond the spawn band + the deepest shape offset.
      z = Math.min(z, BALANCE.proj.farZ + pad + F.wedgeDepth);
    }
    const def = getEnemy(id);
    const e: LiveEnemy = {
      id: this.nextId++,
      def: id,
      pos: { x: lane, z, y: 0 },
      hp: def.hp,
      maxHp: def.hp,
      dead: false,
      sx: 0,
      sy: 0,
      scale: 0,
      spawnZ: z,
    };
    // A fraction of plain marchers fan out to a lateral edge (a pincer), so the
    // filler horde isn't a static column. Deterministic via ctx.rng. We roll
    // unconditionally so the RNG stream is stable regardless of behaviour.
    const flankRoll = ctx.rng.chance(BALANCE.enemies.behavior.marchFlankChance);
    if (getBehavior(def) === 'march' && flankRoll) {
      e.flankTargetX = lane >= 0 ? BALANCE.proj.halfWidth : -BALANCE.proj.halfWidth;
    }
    // Per-unit leg-speed jitter: same-type units spread out as they advance
    // (eager ones pull ahead, stragglers lag) instead of stacking in a rank.
    const J = BALANCE.enemies.behavior.speedJitter;
    e.speedMult = J.min + ctx.rng.next() * (J.max - J.min);
    ctx.enemies.push(e);
    ctx.bus.emit('enemy:spawned', { id: e.id, def: id });
  }

  /** Live enemy projectiles in flight (render layer reads for tracers). */
  projectileCount(): number {
    return this.projectiles.length;
  }

  liveCount(): number {
    return this.ctx.enemies.length;
  }

  allWavesCleared(): boolean {
    return (
      this.waves.every((w) => w.fired) &&
      this.pendingSpawns.length === 0 &&
      this.ctx.enemies.length === 0
    );
  }

  destroy(): void {
    /* list owned by ctx; GameScene drops it */
    this.projectiles.length = 0;
    this.pendingSpawns.length = 0;
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
